import { Card, EmptyState, Table, TableBody, TableCell, TableHeader, TableRow } from '@catering/ui';
import { ChefHat } from 'lucide-react';
import type { JSX } from 'react';

import type { ConsolidatedProduction, ConsolidatedSheetStatus } from '../../../production/api';
import type { CatalogNameMap } from '../../../production/use-catalog-names';
import { ProductionLineTable } from './production-line-table';

const SHEET_STATUS_LABELS: Record<ConsolidatedSheetStatus, string> = {
  SIN_PLANILLA: 'Sin planilla',
  PROVISIONAL: 'Provisoria',
  CERRADA: 'Cerrada',
};

export interface ConsolidatedProductionViewProps {
  view: ConsolidatedProduction;
  serviceTypeNames: CatalogNameMap;
  dietaryTagNames: CatalogNameMap;
}

/**
 * `production-sheets/spec.md` → "Todos los puntos": the read-only consolidated table with the
 * per-unit breakdown and each unit's sheet status. Closing and addenda stay per unit, so this
 * view renders no action at all.
 */
export function ConsolidatedProductionView({
  view,
  serviceTypeNames,
  dietaryTagNames,
}: ConsolidatedProductionViewProps): JSX.Element {
  if (view.items.length === 0 && view.byUnit.length === 0) {
    return <EmptyState icon={ChefHat} title="No hay pedidos para esta fecha" />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-col">
        <span className="text-xs font-medium text-slate-500">Unidades totales</span>
        <span className="text-xl font-semibold tabular-nums text-slate-900">
          {view.totals.totalQuantity}
        </span>
      </Card>

      <Table>
        <caption className="sr-only">Desglose por punto de venta</caption>
        <thead>
          <tr>
            <TableHeader>Punto de venta</TableHeader>
            <TableHeader>Planilla</TableHeader>
            <TableHeader>Cantidad</TableHeader>
          </tr>
        </thead>
        <TableBody>
          {view.byUnit.map((unit) => (
            <TableRow key={unit.operatingUnitId}>
              <TableCell>{unit.name}</TableCell>
              <TableCell>{SHEET_STATUS_LABELS[unit.sheetStatus]}</TableCell>
              <TableCell className="font-medium tabular-nums">{unit.totalQuantity}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {view.items.length > 0 ? (
        <ProductionLineTable
          lines={view.items}
          serviceTypeNames={serviceTypeNames}
          dietaryTagNames={dietaryTagNames}
        />
      ) : null}
    </div>
  );
}
